import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { formatCPF, formatPhone, unmask } from '../../utils/formatters';

const RegisterContainer = styled.div`
  max-width: 500px;
  margin: 40px auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 2rem;
  color: #2d3748;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  
  -webkit-text-security: none !important;

  &[type="password"] {
    -webkit-text-security: disc !important;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary || '#a0522d'};
    box-shadow: 0 0 0 3px ${props => props.theme.primary ? `${props.theme.primary}1A` : 'rgba(160, 82, 45, 0.1)'};
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary || '#a0522d'};
    box-shadow: 0 0 0 3px ${props => props.theme.primary ? `${props.theme.primary}1A` : 'rgba(160, 82, 45, 0.1)'};
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: ${props => props.theme.primary || '#a0522d'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 1rem;

  &:hover {
    filter: brightness(0.9);
  }

  &:disabled {
    background-color: #cbd5e0;
    cursor: not-allowed;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: #718096;

  a {
    color: ${props => props.theme.primary || '#a0522d'};
    font-weight: 600;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const EyeButton = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  transition: color 0.2s;

  &:hover {
    color: #4a5568;
  }

  i {
    font-size: 1.1rem;
  }
`;

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    gender: 'MALE' as 'MALE' | 'FEMALE',
    birthDate: '',
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    if (name === 'cpf') {
      formattedValue = formatCPF(value);
    } else if (name === 'phone') {
      formattedValue = formatPhone(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem.');
      setIsLoading(false);
      return;
    }

    try {
      const success = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        gender: formData.gender,
        birthDate: formData.birthDate,
        cpf: unmask(formData.cpf),
        phone: unmask(formData.phone),
      });
      if (success) {
        toast.success('Cadastro realizado com sucesso!');
        navigate('/'); // Redirect to home/menu after successful registration and login
      } else {
        // Cadastro OK, mas o auto-login não completou.
        toast.success('Cadastro realizado! Faça login para continuar.');
        navigate('/login');
      }
    } catch (err: any) {
      const backendMessage = err?.response?.data?.message;
      toast.error(backendMessage || 'Erro ao realizar cadastro. Verifique os dados e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <RegisterContainer>
        <Title>Criar Conta</Title>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Nome Completo</Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Digite seu nome completo"
              required
            />
          </FormGroup>

          <Row>
            <FormGroup>
              <Label htmlFor="cpf">CPF</Label>
              <Input
                id="cpf"
                name="cpf"
                type="text"
                value={formData.cpf}
                onChange={handleChange}
                placeholder="000.000.000-00"
                required
                maxLength={14}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="phone">Celular</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
                required
                maxLength={15}
              />
            </FormGroup>
          </Row>

          <FormGroup>
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="seu@email.com"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Senha</Label>
            <PasswordWrapper>
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
                style={{ width: '100%', paddingRight: '2.5rem' }}
              />
              <EyeButton type="button" onClick={() => setShowPassword(!showPassword)}>
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </EyeButton>
            </PasswordWrapper>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirmar Senha</Label>
            <PasswordWrapper>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repita sua senha"
                required
                minLength={6}
                style={{ width: '100%', paddingRight: '2.5rem' }}
              />
              <EyeButton type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </EyeButton>
            </PasswordWrapper>
          </FormGroup>

          <Row>
            <FormGroup>
              <Label htmlFor="gender">Gênero</Label>
              <Select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <option value="MALE">Masculino</option>
                <option value="FEMALE">Feminino</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="birthDate">Data de Nascimento</Label>
              <Input
                id="birthDate"
                name="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </Row>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Cadastrando...' : 'Criar Conta'}
          </Button>
        </Form>
        <LoginLink>
          Já tem uma conta? <Link to="/login">Entrar</Link>
        </LoginLink>
      </RegisterContainer>
    </Layout>
  );
};

export default RegisterPage;
